// Shared helpers for Admin Panel sub-components.
// Extracted so the once-monolithic AdminPanel.jsx can stay small enough for the
// Babel visual-edits metadata plugin to traverse without stack overflow.

export const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Legacy localStorage token — kept as a fallback during the migration to
// httpOnly cookies. Backend accepts either. New logins get both: the JSON
// token is still stored here for downstream fetches that haven't been
// updated to use `credentials: 'include'`, and the httpOnly cookie shields
// the same token from XSS.
export const getToken = () => localStorage.getItem('admin_token');
export const setToken = (token) => localStorage.setItem('admin_token', token);
export const removeToken = () => localStorage.removeItem('admin_token');

// Read a cookie by name. Returns null if the cookie isn't set.
// Non-httpOnly `csrf_token` is set alongside the auth cookie at login so
// same-origin JS can echo it back on mutating requests (double-submit).
export const readCookie = (name) => {
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = document.cookie.split(';').map((p) => p.trim());
  const hit = parts.find((p) => p.startsWith(prefix));
  return hit ? decodeURIComponent(hit.slice(prefix.length)) : null;
};

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Parse a JSON body; a non-JSON reply (Cloudflare/ingress 5xx HTML while the
// backend restarts or is unreachable) becomes a readable error instead of "JSON.parse: unexpected character".
export const parseJson = async (response) => {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    const status = response.status;
    return { detail: status >= 500 || status === 0
      ? `Server is unavailable or restarting (HTTP ${status}). Please try again in a minute.`
      : `Unexpected server response (HTTP ${status}).` };
  }
};

// API helper with auth. Always sends cookies (`credentials: 'include'`) and,
// where present, also attaches the legacy Bearer token so pre-cookie sessions
// still work. On mutating requests it also attaches the CSRF header.
// Throws on non-2xx with the backend `detail`.
export const apiCall = async (endpoint, options = {}) => {
  const method = (options.method || 'GET').toUpperCase();
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  if (MUTATING.has(method)) {
    const csrf = readCookie('csrf_token');
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    method,
    credentials: 'include',
    headers,
  });
  const data = await parseJson(response);
  if (response.status === 401 && !endpoint.startsWith('/api/auth/')) {
    // Session genuinely expired/invalid → let AdminPanel drop to the login screen instead of scattering red errors.
    window.dispatchEvent(new CustomEvent('admin-unauthorized', { detail: { endpoint, message: data.detail } }));
  }
  if (!response.ok) throw new Error(data.detail || 'API request failed');
  return data;
};

// Multipart upload (photos, scans). Auth + CSRF like apiCall; do not set Content-Type (browser adds the boundary).
export const uploadFile = async (endpoint, file, field = 'file', extra = {}) => {
  const fd = new FormData();
  fd.append(field, file);
  Object.entries(extra).forEach(([k, v]) => fd.append(k, String(v)));
  const token = getToken();
  const csrf = readCookie('csrf_token');
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST', body: fd, credentials: 'include',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
  });
  const data = await parseJson(response);
  if (!response.ok) throw new Error(data.detail || 'Upload failed');
  return data;
};
